import {
  AdminVenue,
  EventItem,
  EventOccurrence,
  TicketTier,
  VenueCreatePayload,
  VenueUpdatePayload,
} from '../types';
import { normalizeOccurrence } from '../utils/occurrence';
import { API_ENDPOINTS, getApiUrl, getAuthHeaders } from '../config/api';
import { normalizePaymentCreateOrderPayload } from '../config/payu';
import { buildAdminEventExtraData, safeHttpUrl } from '../utils/adminEventExtraData';
import {
  coalescePriceFromApiAndTiers,
  mapRawApiEventToEventItem,
  normalizeMediaRow,
} from '../utils/eventMedia';

/**
 * AUTHENTICATION
 */
export const authApi = {
  sendOtp: async (email: string, name?: string): Promise<boolean> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.SEND_OTP), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ email, name }),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to send OTP (${response.status}: ${response.statusText})`);
      }
      return true;
    } catch (error) {
      console.error('[API Error] sendOtp:', error);
      throw error;
    }
  },
  
  verifyOtp: async (
    email: string,
    otp: string,
    extra?: { name?: string; phone?: string }
  ): Promise<{ token?: string, access_token?: string, user: any }> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.VERIFY_OTP), {
        method: 'POST',
        headers: getAuthHeaders(),
        // full_name/phone are persisted only when this verify auto-creates the
        // account (guest checkout); ignored for existing accounts.
        body: JSON.stringify({ email, code: otp, full_name: extra?.name, phone: extra?.phone }),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to verify OTP (${response.status}: ${response.statusText})`);
      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] verifyOtp:', error);
      throw error;
    }
  },

  signInWithPassword: async (email: string, password: string): Promise<{ token?: string, access_token?: string, user: any }> => {
    try {
      // Backend AccountInLogin requires username, email, and password.
      // We use email as username for compatibility, except for the admin account.
      const username = email.toLowerCase() === 'admin@zoniq.com' ? 'admin' : email;
      
      const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.SIGNIN), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ username, email, password }),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
        let errorMsg = errorData.detail || `Failed to sign in (${response.status}: ${response.statusText})`;
        if (Array.isArray(errorMsg)) errorMsg = errorMsg[0]?.msg || 'Validation error';
        throw new Error(errorMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] signInWithPassword:', error);
      throw error;
    }
  },

  signUpWithPassword: async (userData: any): Promise<{ token?: string, access_token?: string, user: any }> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.SIGNUP), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
        let errorMsg = errorData.detail || `Failed to sign up (${response.status}: ${response.statusText})`;
        if (Array.isArray(errorMsg)) errorMsg = errorMsg[0]?.msg || 'Validation error';
        throw new Error(errorMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] signUpWithPassword:', error);
      throw error;
    }
  },

  forgotPassword: async (email: string): Promise<boolean> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.FORGOT_PASSWORD), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to request password reset (${response.status}: ${response.statusText})`);
      }
      return true;
    } catch (error) {
      console.error('[API Error] forgotPassword:', error);
      throw error;
    }
  },

  resetPassword: async (email: string, otp: string, newPassword: string): Promise<boolean> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.RESET_PASSWORD), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ email, code: otp, newPassword: newPassword }),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to reset password (${response.status}: ${response.statusText})`);
      }
      return true;
    } catch (error) {
      console.error('[API Error] resetPassword:', error);
      throw error;
    }
  },

  logout: async (): Promise<boolean> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.LOGOUT), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to logout (${response.status}: ${response.statusText})`);

      }
      return true;
    } catch (error) {
      console.error('[API Error] logout:', error);
      return false; 
    }
  }
};

/**
 * EVENTS & INVENTORY
 */
export function mapCategoryToTier(c: any): TicketTier {
  const occIds = c.occurrenceIds ?? c.occurrence_ids;
  return {
    id: c.id.toString(),
    name: String(c.name ?? ''),
    price: Number(c.price ?? c.ticket_price ?? c.base_price ?? 0),
    available: Number(c.availableSeats ?? c.available_seats ?? c.totalSeats ?? c.total_seats ?? 0),
    capacityPerOccurrence:
      c.capacityPerOccurrence != null || c.capacity_per_occurrence != null
        ? Number(c.capacityPerOccurrence ?? c.capacity_per_occurrence)
        : undefined,
    description: c.description != null ? String(c.description) : undefined,
    isPass: Boolean(c.isPass ?? c.is_pass),
    coversAllOccurrences: c.coversAllOccurrences ?? c.covers_all_occurrences ?? true,
    occurrenceIds: Array.isArray(occIds) ? occIds.map(String) : undefined,
  };
}

async function buildEventItemFromDetailPayload(data: any): Promise<EventItem | null> {
  if (!data || data.id == null) return null;

  let categories = data.categories || [];
  if (categories.length === 0 && data.id) {
    try {
      const catResponse = await fetch(getApiUrl(API_ENDPOINTS.EVENTS.EVENT_CATEGORIES(data.id.toString())), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (catResponse.ok) {
        categories = await catResponse.json();
      }
    } catch (catError) {
      console.warn('[API Warning] Failed to fetch categories for event:', catError);
    }
  }

  const base = mapRawApiEventToEventItem(data);
  const tiers: TicketTier[] = categories.map(mapCategoryToTier);
  return {
    ...base,
    tiers,
    tierCount: tiers.length > 0 ? tiers.length : base.tierCount,
    priceFrom: coalescePriceFromApiAndTiers(base.priceFrom, tiers),
  };
}

export const eventsApi = {
  /**
   * Tiers for an event. With `occurrenceId` the price/availability are that date/slot's own
   * (a tier not offered there is omitted); pass tiers are always included.
   */
  getEventTiers: async (eventId: string, occurrenceId?: string): Promise<TicketTier[]> => {
    let url = getApiUrl(API_ENDPOINTS.EVENTS.EVENT_CATEGORIES(eventId));
    if (occurrenceId) url += `${url.includes('?') ? '&' : '?'}occurrence_id=${encodeURIComponent(occurrenceId)}`;
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to load tickets (${response.status})`);
    }
    const rows = await response.json();
    return (Array.isArray(rows) ? rows : []).map(mapCategoryToTier);
  },

  /** Occurrences of an event with per-tier availability; `from`/`to` window long schedules. */
  getEventOccurrences: async (
    eventId: string,
    opts: { from?: string; to?: string; includePast?: boolean } = {}
  ): Promise<EventOccurrence[]> => {
    const qs = new URLSearchParams();
    if (opts.from) qs.set('from', opts.from);
    if (opts.to) qs.set('to', opts.to);
    if (opts.includePast) qs.set('includePast', 'true');
    const q = qs.toString();
    const url = getApiUrl(API_ENDPOINTS.EVENTS.OCCURRENCES(eventId)) + (q ? `?${q}` : '');
    const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to load dates (${response.status})`);
    }
    const data = await response.json();
    return (data.occurrences ?? []).map(normalizeOccurrence);
  },

  getAllEvents: async (city?: string): Promise<EventItem[]> => {
    try {
      let url = getApiUrl(API_ENDPOINTS.EVENTS.LIST);
      const params = new URLSearchParams();
      if (city && city !== 'All Cities') params.append('city', city);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch events (${response.status}: ${response.statusText})`);
      }
      const data = await response.json();
      const eventsList = data.events !== undefined ? data.events : data;
      return eventsList.map((e: any) => mapRawApiEventToEventItem(e));
    } catch (error) {
      console.error('[API Error] getAllEvents:', error);
      throw error;
    }
  },

  getEventBySlug: async (slug: string): Promise<EventItem | null> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.EVENTS.DETAIL(slug)), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (response.status === 404) return null;
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to fetch event details (${response.status}: ${response.statusText})`);
      }
      const data = await response.json();
      return buildEventItemFromDetailPayload(data);
    } catch (error) {
      console.error('[API Error] getEventBySlug:', error);
      throw error;
    }
  },

  /** Same detail payload as by slug; `id` must match public events API path. */
  getEventById: async (id: string): Promise<EventItem | null> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.EVENTS.DETAIL(id)), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (response.status === 404) return null;
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to fetch event details (${response.status}: ${response.statusText})`);
      }
      const data = await response.json();
      return buildEventItemFromDetailPayload(data);
    } catch (error) {
      console.error('[API Error] getEventById:', error);
      throw error;
    }
  },

  checkInventory: async (eventId: string): Promise<any> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.EVENTS.SEATS(eventId)), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to check inventory (${response.status}: ${response.statusText})`);

      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] checkInventory:', error);
      throw error;
    }
  },

  /** Public seat map + availability for an event (optional category filter). */
  getEventSeats: async (eventId: string, categoryId?: string): Promise<any> => {
    try {
      let url = getApiUrl(API_ENDPOINTS.EVENTS.SEATS(eventId));
      if (categoryId) {
        url += `${url.includes('?') ? '&' : '?'}category_id=${encodeURIComponent(categoryId)}`;
      }
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Failed to load seats (${response.status}: ${response.statusText})`
        );
      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] getEventSeats:', error);
      throw error;
    }
  },

  createEvent: async (data: any, token?: string): Promise<any> => {
    try {
      const authToken = token || localStorage.getItem('zoniq_token');
      if (!authToken) throw new Error('Not authenticated');

      const payload = {
        title: data.title,
        description: data.description,
        category: data.category,
        venueId: data.venueId,
        // Single-date events send eventDate; multi-slot / multi-day / recurring send a schedule
        // (the server derives eventDate from its first occurrence).
        eventDate:
          !data.schedule && data.date && data.time
            ? new Date(`${data.date}T${data.time}`).toISOString()
            : undefined,
        scheduleType: data.scheduleType && data.scheduleType !== 'single' ? data.scheduleType : undefined,
        schedule: data.schedule || undefined,
        bookingStartDate: data.bookingStartDate,
        bookingEndDate: data.bookingEndDate,
        bannerImageUrl: safeHttpUrl(data.image),
        organizerName: data.organizerName,
        organizerContact: data.organizerContact,
        termsAndConditions: data.termsAndConditions,
        extraData: buildAdminEventExtraData(data as Record<string, unknown>),
      };

      const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.BASE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to create event (${response.status}: ${response.statusText})`);
      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] createEvent:', error);
      throw error;
    }
  },

  updateEvent: async (data: any, token?: string): Promise<any> => {
    try {
      const authToken = token || localStorage.getItem('zoniq_token');
      if (!authToken) throw new Error('Not authenticated');

      const payload = {
        title: data.title,
        description: data.description,
        category: data.category,
        venueId: data.venueId,
        eventDate: data.date && data.time ? new Date(`${data.date}T${data.time}`).toISOString() : undefined,
        bookingStartDate: data.bookingStartDate,
        bookingEndDate: data.bookingEndDate,
        bannerImageUrl: safeHttpUrl(data.image),
        organizerName: data.organizerName,
        organizerContact: data.organizerContact,
        termsAndConditions: data.termsAndConditions,
        extraData: buildAdminEventExtraData(data as Record<string, unknown>),
      };

      const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.DETAIL(data.id)), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to update event (${response.status}: ${response.statusText})`);
      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] updateEvent:', error);
      throw error;
    }
  },

  listEventMedia: async (
    eventId: string,
    token?: string,
    activeOnly = false
  ): Promise<{ items: import('../types').EventMediaItem[]; total: number }> => {
    const authToken = token || localStorage.getItem('zoniq_token');
    if (!authToken) throw new Error('Not authenticated');
    const qs = activeOnly ? '?active_only=true' : '';
    const response = await fetch(
      getApiUrl(`${API_ENDPOINTS.ADMIN.EVENTS.MEDIA(eventId)}${qs}`),
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || `Failed to list event media (${response.status})`
      );
    }
    const data = await response.json();
    const rawItems = Array.isArray(data.items) ? data.items : [];
    const items = rawItems.map((row: any) => normalizeMediaRow(row));
    return { items, total: data.total ?? items.length };
  },

  uploadEventMedia: async (
    eventId: string,
    file: File,
    mediaType: 'image' | 'video',
    sortOrder = 0,
    token?: string,
    title?: string,
    altText?: string
  ): Promise<import('../types').EventMediaItem> => {
    const authToken = token || localStorage.getItem('zoniq_token');
    if (!authToken) throw new Error('Not authenticated');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('media_type', mediaType);
    formData.append('sort_order', String(sortOrder));
    if (title) formData.append('title', title);
    if (altText) formData.append('alt_text', altText);
    const response = await fetch(
      getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.MEDIA_UPLOAD(eventId)),
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData,
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || `Failed to upload media (${response.status})`
      );
    }
    const row = await response.json();
    return normalizeMediaRow(row);
  },

  createEventMedia: async (
    eventId: string,
    body: {
      mediaUrl: string;
      mediaType: 'image' | 'video' | 'audio' | 'other';
      sortOrder?: number;
      title?: string;
      altText?: string;
      isActive?: boolean;
    },
    token?: string
  ): Promise<import('../types').EventMediaItem> => {
    const authToken = token || localStorage.getItem('zoniq_token');
    if (!authToken) throw new Error('Not authenticated');
    const response = await fetch(
      getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.MEDIA(eventId)),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          mediaUrl: body.mediaUrl,
          mediaType: body.mediaType,
          sortOrder: body.sortOrder ?? 0,
          title: body.title,
          altText: body.altText,
          isActive: body.isActive ?? true,
        }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || `Failed to create media (${response.status})`
      );
    }
    const row = await response.json();
    return normalizeMediaRow(row);
  },

  updateEventMedia: async (
    eventId: string,
    mediaId: string,
    body: Partial<{
      mediaUrl: string;
      mediaType: 'image' | 'video' | 'audio' | 'other';
      sortOrder: number;
      title: string;
      altText: string;
      isActive: boolean;
    }>,
    token?: string
  ): Promise<import('../types').EventMediaItem> => {
    const authToken = token || localStorage.getItem('zoniq_token');
    if (!authToken) throw new Error('Not authenticated');
    const response = await fetch(
      getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.MEDIA_ITEM(eventId, mediaId)),
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || `Failed to update media (${response.status})`
      );
    }
    const row = await response.json();
    return normalizeMediaRow(row);
  },

  deleteEventMedia: async (
    eventId: string,
    mediaId: string,
    token?: string
  ): Promise<void> => {
    const authToken = token || localStorage.getItem('zoniq_token');
    if (!authToken) throw new Error('Not authenticated');
    const response = await fetch(
      getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.MEDIA_ITEM(eventId, mediaId)),
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || `Failed to delete media (${response.status})`
      );
    }
  },

  deleteEvent: async (id: string, token?: string): Promise<boolean> => {
    try {
      const authToken = token || localStorage.getItem('zoniq_token');
      const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.DETAIL(id)), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (!response.ok) {
        // Try to get more specific error from backend
        const errorData = await response.json().catch(() => ({ detail: 'Failed to delete event without specific error message.' }));
        throw new Error(errorData.detail || `Failed to delete event (${response.status}: ${response.statusText})`);
      }
      return true;
    } catch (error) {
      console.error('[API Error] deleteEvent:', error);
      throw error;
    }
  }
};

/**
 * VENUES (admin) - see backend `admin/venues` routes.
 */
export interface VenueListQuery {
  page?: number;
  page_size?: number;
  city?: string;
  is_active?: boolean;
  search?: string;
}

/** Backend returns camelCase (`VenueResponse` / `VenueDetailResponse`); app uses `AdminVenue` (snake_case). */
function normalizeAdminVenueFromApi(raw: Record<string, unknown>): AdminVenue {
  const id = raw.id as number;
  const name = raw.name as string;
  const isActive = raw.is_active ?? raw.isActive;
  return {
    id,
    name,
    address: (raw.address as string | null | undefined) ?? null,
    city: (raw.city as string | null | undefined) ?? null,
    state: (raw.state as string | null | undefined) ?? null,
    country: (raw.country as string | null | undefined) ?? null,
    pincode: (raw.pincode as string | null | undefined) ?? null,
    capacity: (raw.capacity as number | null | undefined) ?? null,
    seat_map_config:
      (raw.seat_map_config as Record<string, unknown> | null | undefined) ??
      (raw.seatMapConfig as Record<string, unknown> | null | undefined) ??
      null,
    contact_phone:
      (raw.contact_phone as string | null | undefined) ??
      (raw.contactPhone as string | null | undefined) ??
      null,
    contact_email:
      (raw.contact_email as string | null | undefined) ??
      (raw.contactEmail as string | null | undefined) ??
      null,
    is_active: Boolean(isActive),
    created_at:
      (raw.created_at as string | undefined) ??
      (raw.createdAt as string | undefined) ??
      '',
    updated_at:
      (raw.updated_at as string | null | undefined) ??
      (raw.updatedAt as string | null | undefined) ??
      null,
  };
}

/** OpenAPI `VenueCreate` / `VenueUpdate` use camelCase field names. */
function venueCreatePayloadToApiBody(data: VenueCreatePayload): Record<string, unknown> {
  const body: Record<string, unknown> = { name: data.name };
  if (data.address !== undefined) body.address = data.address;
  if (data.city !== undefined) body.city = data.city;
  if (data.state !== undefined) body.state = data.state;
  if (data.country !== undefined) body.country = data.country;
  if (data.pincode !== undefined) body.pincode = data.pincode;
  if (data.capacity !== undefined) body.capacity = data.capacity;
  if (data.contact_phone !== undefined) body.contactPhone = data.contact_phone;
  if (data.contact_email !== undefined) body.contactEmail = data.contact_email;
  if (data.seat_map_config !== undefined) body.seatMapConfig = data.seat_map_config;
  return body;
}

function venueUpdatePayloadToApiBody(data: VenueUpdatePayload): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.name !== undefined) body.name = data.name;
  if (data.address !== undefined) body.address = data.address;
  if (data.city !== undefined) body.city = data.city;
  if (data.state !== undefined) body.state = data.state;
  if (data.country !== undefined) body.country = data.country;
  if (data.pincode !== undefined) body.pincode = data.pincode;
  if (data.capacity !== undefined) body.capacity = data.capacity;
  if (data.contact_phone !== undefined) body.contactPhone = data.contact_phone;
  if (data.contact_email !== undefined) body.contactEmail = data.contact_email;
  if (data.seat_map_config !== undefined) body.seatMapConfig = data.seat_map_config;
  if (data.is_active !== undefined) body.isActive = data.is_active;
  return body;
}

export const venueApi = {
  /** GET /admin/venues/all - active venues (dropdowns). */
  getVenues: async (token?: string): Promise<AdminVenue[]> => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.VENUES.ALL), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch venues (${response.status}: ${response.statusText})`);
    }
    const rows = (await response.json()) as unknown[];
    return rows.map((r) => normalizeAdminVenueFromApi(r as Record<string, unknown>));
  },

  /** GET /admin/venues - paginated list with filters. */
  listVenues: async (query: VenueListQuery = {}, token?: string) => {
    const params = new URLSearchParams();
    if (query.page != null) params.set('page', String(query.page));
    if (query.page_size != null) params.set('page_size', String(query.page_size));
    if (query.city) params.set('city', query.city);
    if (query.is_active != null) params.set('is_active', String(query.is_active));
    if (query.search) params.set('search', query.search);
    const qs = params.toString();
    const path = qs ? `${API_ENDPOINTS.ADMIN.VENUES.BASE}?${qs}` : API_ENDPOINTS.ADMIN.VENUES.BASE;
    const response = await fetch(getApiUrl(path), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to list venues (${response.status}: ${response.statusText})`);
    }
    const data = (await response.json()) as {
      venues?: unknown[];
      total: number;
      page: number;
      page_size?: number;
      pageSize?: number;
    };
    const venues = (data.venues ?? []).map((r) =>
      normalizeAdminVenueFromApi(r as Record<string, unknown>)
    );
    return {
      venues,
      total: data.total,
      page: data.page,
      page_size: data.page_size ?? data.pageSize ?? venues.length,
    };
  },

  getVenue: async (id: string, token?: string): Promise<AdminVenue> => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.VENUES.DETAIL(id)), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch venue (${response.status}: ${response.statusText})`);
    }
    const raw = (await response.json()) as Record<string, unknown>;
    return normalizeAdminVenueFromApi(raw);
  },

  createVenue: async (data: VenueCreatePayload, token?: string): Promise<AdminVenue> => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.VENUES.BASE), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(venueCreatePayloadToApiBody(data)),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to create venue (${response.status}: ${response.statusText})`);
    }
    const raw = (await response.json()) as Record<string, unknown>;
    return normalizeAdminVenueFromApi(raw);
  },

  updateVenue: async (id: string, data: VenueUpdatePayload, token?: string): Promise<AdminVenue> => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.VENUES.DETAIL(id)), {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify(venueUpdatePayloadToApiBody(data)),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to update venue (${response.status}: ${response.statusText})`);
    }
    const raw = (await response.json()) as Record<string, unknown>;
    return normalizeAdminVenueFromApi(raw);
  },

  deleteVenue: async (id: string, options?: { permanent?: boolean }, token?: string): Promise<{ message: string }> => {
    const permanent = options?.permanent === true;
    const url = `${getApiUrl(API_ENDPOINTS.ADMIN.VENUES.DETAIL(id))}?permanent=${permanent}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to delete venue (${response.status}: ${response.statusText})`);
    }
    return await response.json();
  },
};

/**
 * SEAT CATEGORIES (TIERS)
 */
export const seatApi = {
  createCategory: async (eventId: string, data: any, token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.CATEGORIES(eventId)), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to create seat category (${response.status}: ${response.statusText})`);
    }
    return await response.json();
  },

  updateCategory: async (categoryId: string, data: any, token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.CATEGORY_DETAIL(categoryId)), {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to update seat category (${response.status}: ${response.statusText})`);
    }
    return await response.json();
  },

  listCategoriesForEvent: async (eventId: string, token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.CATEGORIES(eventId)), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || `Failed to list seat categories (${response.status}: ${response.statusText})`
      );
    }
    return await response.json();
  },

  /** Remove tier; if seats were sold, backend rejects - then soft-deactivate. */
  deleteCategory: async (categoryId: string, token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.CATEGORY_DETAIL(categoryId)), {
      method: 'DELETE',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const patchRes = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.CATEGORY_DETAIL(categoryId)), {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ isActive: false }),
      });
      if (!patchRes.ok) {
        const errorData = await patchRes.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Failed to remove seat category (${patchRes.status})`
        );
      }
      return { deactivated: true };
    }
    return await response.json().catch(() => ({}));
  },
};

/**
 * CHECKOUT & PAYMENTS
 */
export const paymentApi = {
  createOrder: async (
    bookingId: number,
    token?: string,
    expectedAmountInr?: string
  ): Promise<any> => {
    try {
      const body: Record<string, unknown> = { bookingId };
      if (expectedAmountInr !== undefined) {
        body.expectedAmountInr = expectedAmountInr;
      }
      const response = await fetch(getApiUrl(API_ENDPOINTS.PAYMENTS.CREATE_ORDER), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(body),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to create payment order (${response.status}: ${response.statusText})`);
      }
      const raw = await response.json();
      return normalizePaymentCreateOrderPayload(raw);
    } catch (error) {
      console.error('[API Error] createOrder:', error);
      throw error;
    }
  },

  verifyPayment: async (paymentDetails: any): Promise<boolean> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.PAYMENTS.VERIFY), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(paymentDetails),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to verify payment (${response.status}: ${response.statusText})`);

      }
      return true;
    } catch (error) {
      console.error('[API Error] verifyPayment:', error);
      throw error;
    }
  },

  /** After PayU redirect; use when failure page has booking_id (webhook may have captured payment). */
  getBookingPaymentStatus: async (bookingId: string, token?: string): Promise<{
    booking_id: number;
    payment_status: string;
    payment: { status: string } | null;
  }> => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.PAYMENTS.BOOKING_STATUS(bookingId)), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to load payment status (${response.status})`);
    }
    return response.json();
  },
};

/**
 * USER DATA
 */
export const userApi = {
  getAccount: async (token?: string): Promise<any> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER.ACCOUNT), {
        method: 'GET',
        headers: getAuthHeaders(token),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch account info (${response.status}: ${response.statusText})`);

      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] getAccount:', error);
      throw error;
    }
  },

  getProfile: async (token?: string): Promise<any> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER.PROFILE), {
        method: 'GET',
        headers: getAuthHeaders(token),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch user profile (${response.status}: ${response.statusText})`);

      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] getProfile:', error);
      throw error;
    }
  },
  
  /** PATCH /users/me - account fields (username, email, phone, full_name). */
  updateAccount: async (data: any, token?: string): Promise<any> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER.ACCOUNT), {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Failed to update account (${response.status}: ${response.statusText})`
        );
      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] updateAccount:', error);
      throw error;
    }
  },

  /** PATCH /users/me/profile - profile table (full_name, phone, DOB, state, city). */
  updateProfile: async (data: any, token?: string): Promise<any> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER.PROFILE), {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to update user profile (${response.status}: ${response.statusText})`);
      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] updateProfile:', error);
      throw error;
    }
  },

  uploadProfilePicture: async (file: File, token?: string): Promise<any> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const authToken = token || localStorage.getItem('zoniq_token');
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER.PROFILE_PICTURE), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData,
      });

      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to upload profile picture (${response.status}: ${response.statusText})`);
      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] uploadProfilePicture:', error);
      throw error;
    }
  },

  deleteProfilePicture: async (token?: string): Promise<{ profile_pic_url: string | null; message: string }> => {
    try {
      const authToken = token || localStorage.getItem('zoniq_token');
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER.PROFILE_PICTURE), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Failed to remove profile picture (${response.status}: ${response.statusText})`
        );
      }
      return await response.json();
    } catch (error) {
      console.error('[API Error] deleteProfilePicture:', error);
      throw error;
    }
  },

  getBookings: async (token?: string): Promise<any[]> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER.BOOKINGS), {
        method: 'GET',
        headers: getAuthHeaders(token),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch user bookings (${response.status}: ${response.statusText})`);

      }
      const data = await response.json();
      return data.bookings || [];
    } catch (error) {
      console.error('[API Error] getBookings:', error);
      throw error;
    }
  },

  cancelBooking: async (bookingId: string, token?: string): Promise<boolean> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.BOOKINGS.CANCEL(bookingId)), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ reason: 'User requested cancellation' }),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to cancel booking (${response.status}: ${response.statusText})`);

      }
      return true;
    } catch (error) {
      console.error('[API Error] cancelBooking:', error);
      throw error;
    }
  },

  getTickets: async (token?: string): Promise<any[]> => {
    return userApi.getBookings(token);
  },

  cancelTicket: async (token: string, bookingId: string): Promise<boolean> => {
    return userApi.cancelBooking(bookingId, token);
  },

  joinWaitlist: async (eventId: string, tierId: string, token?: string): Promise<boolean> => {
    try {
      const response = await fetch(getApiUrl(`/api/events/${eventId}/waitlist`), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ category_id: parseInt(tierId) }),
      });
      if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to join waitlist (${response.status}: ${response.statusText})`);

      }
      return true;
    } catch (error) {
      console.error('[API Error] joinWaitlist:', error);
      throw error;
    }
  }
};

export * from './cartApi';
export * from './promoApi';
export * from './ticketApi';
export * from './queueApi';
export * from './wishlistApi';
export * from './adminApi';
export * from './userSettingsApi';
export * from './aqiApi';
